import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import FormInput from '../../../components/ui/FormInput';
import Icon from '../../../components/AppIcon';
import { showErrorToast, showSuccessToast, showLoadingToast, dismissToast } from '../../../utils/toast';
import { validators } from '../../../utils/formValidators';
import { withRetryAndOfflineQueue } from '../../../utils/retryWithBackoff';
import { announceToScreenReader } from '../../../utils/ariaUtils';
import AriaLiveRegion from '../../../components/ui/AriaLiveRegion';

const RetirementForm = ({ plan, onSubmit, onClose, calculating }) => {
  const validationRules = (values) => {
    const errors = {};
    
    const nameError = validators?.required(values?.planName, 'Plan name');
    if (nameError) errors.planName = nameError;
    
    const ageError = validators?.age(values?.currentAge, 18, 100);
    if (ageError) errors.currentAge = ageError;
    
    const retAgeError = validators?.age(values?.retirementAge, 50, 75);
    if (retAgeError) {
      errors.retirementAge = retAgeError;
    } else if (values?.retirementAge <= values?.currentAge) {
      errors.retirementAge = 'Retirement age must be greater than current age';
    }
    
    const lifeExpError = validators?.age(values?.lifeExpectancy, 70, 110);
    if (lifeExpError) {
      errors.lifeExpectancy = lifeExpError;
    } else if (values?.lifeExpectancy <= values?.retirementAge) {
      errors.lifeExpectancy = 'Life expectancy must be greater than retirement age';
    }
    
    const incomeError = validators?.positiveNumber(values?.currentAnnualIncome, 'Current annual income');
    if (incomeError) errors.currentAnnualIncome = incomeError;
    
    const expenseError = validators?.positiveNumber(values?.currentExpenses, 'Current expenses');
    if (expenseError) errors.currentExpenses = expenseError;
    
    const ratioError = validators?.percentage(values?.targetIncomeReplacementRatio, 'Income replacement ratio');
    if (ratioError) errors.targetIncomeReplacementRatio = ratioError;
    
    return errors;
  };

  const [values, setValues] = useState({
    planName: 'My Retirement Plan',
    currentAge: 35,
    retirementAge: 65,
    currentAnnualIncome: 75000,
    currentExpenses: 48000,
    targetIncomeReplacementRatio: 70,
    rrspBalance: 125000,
    tfsaBalance: 45000,
    otherSavings: 15000,
    monthlyRrspContribution: 500,
    monthlyTfsaContribution: 300,
    cppEstimatedMonthly: 1200,
    oasEstimatedMonthly: 700,
    employerPensionMonthly: 0,
    inflationRate: 2.5,
    expectedReturnRate: 6.0,
    lifeExpectancy: 90
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    if (plan) {
      setValues({
        planName: plan?.plan_name || 'My Retirement Plan',
        currentAge: plan?.current_age || 35,
        retirementAge: plan?.retirement_age || 65,
        currentAnnualIncome: plan?.current_annual_income || 75000,
        currentExpenses: plan?.current_expenses || 48000,
        targetIncomeReplacementRatio: plan?.target_income_replacement_ratio || 70,
        rrspBalance: plan?.rrsp_balance || 125000,
        tfsaBalance: plan?.tfsa_balance || 45000,
        otherSavings: plan?.other_savings || 15000,
        monthlyRrspContribution: plan?.monthly_rrsp_contribution || 500,
        monthlyTfsaContribution: plan?.monthly_tfsa_contribution || 300,
        cppEstimatedMonthly: plan?.cpp_estimated_monthly || 1200,
        oasEstimatedMonthly: plan?.oas_estimated_monthly || 700,
        employerPensionMonthly: plan?.employer_pension_monthly || 0,
        inflationRate: plan?.inflation_rate || 2.5,
        expectedReturnRate: plan?.expected_return_rate || 6.0,
        lifeExpectancy: plan?.life_expectancy || 90
      });
    }
  }, [plan]);

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    const allErrors = validationRules(values);
    if (allErrors?.[field]) {
      setErrors({ ...errors, [field]: allErrors?.[field] });
    } else {
      const { [field]: removed, ...rest } = errors;
      setErrors(rest);
    }
  };

  const handleChange = (field, value) => {
    setValues({ ...values, [field]: value });
    if (touched?.[field]) {
      const allErrors = validationRules({ ...values, [field]: value });
      if (allErrors?.[field]) {
        setErrors({ ...errors, [field]: allErrors?.[field] });
      } else {
        const { [field]: removed, ...rest } = errors;
        setErrors(rest);
      }
    }
  };

  const validateForm = () => {
    const allErrors = validationRules(values);
    setErrors(allErrors);
    setTouched({
      planName: true,
      currentAge: true,
      retirementAge: true,
      lifeExpectancy: true,
      currentAnnualIncome: true,
      currentExpenses: true,
      targetIncomeReplacementRatio: true
    });
    return Object.keys(allErrors)?.length === 0;
  };

  const isFieldValid = (field) => {
    return touched?.[field] && !errors?.[field] && values?.[field];
  };

  const isFormValid = Object.keys(errors)?.length === 0;

  const errorCount = Object.keys(errors)?.length;
  const touchedCount = Object.keys(touched)?.length;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const validationErrors = validationRules(values);
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      setTouched(Object.keys(values)?.reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      const msg = `Please fix ${Object.keys(validationErrors)?.length} validation error${Object.keys(validationErrors)?.length > 1 ? 's' : ''} before saving`;
      setAnnouncement(msg);
      announceToScreenReader(msg, 'assertive');
      return;
    }
    
    setIsSubmitting(true);
    const loadingToastId = showLoadingToast('Saving retirement plan...');
    
    try {
      await withRetryAndOfflineQueue(
        () => onSubmit(values),
        {},
        { operationName: 'Save retirement plan', maxRetries: 5, baseDelay: 1000 }
      );
      dismissToast(loadingToastId);
      const msg = 'Retirement plan saved successfully';
      showSuccessToast(msg);
      setAnnouncement(msg);
      announceToScreenReader(msg, 'polite');
    } catch (error) {
      dismissToast(loadingToastId);
      if (!error?.isOffline) {
        const msg = 'Failed to save retirement plan. Please try again.';
        showErrorToast(msg, error);
        setAnnouncement(msg);
        announceToScreenReader(msg, 'assertive');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in overflow-y-auto"
      onClick={(e) => {
        if (e?.target === e?.currentTarget && !calculating && !isSubmitting) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-card rounded-xl border shadow-elevation-4 w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-slide-up my-8"
        onClick={(e) => e?.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="retirement-form-title"
      >
        <div className="sticky top-0 bg-card border-b p-4 md:p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
              <Icon name="PiggyBank" size={20} color="var(--color-primary)" strokeWidth={2} aria-hidden="true" />
            </div>
            <div>
              <h2 id="retirement-form-title" className="text-lg md:text-xl font-semibold text-foreground">
                {plan ? 'Edit Retirement Plan' : 'Create Retirement Plan'}
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground">Plan your Canadian retirement</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-all duration-250 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Close retirement plan form"
            disabled={calculating || isSubmitting}
          >
            <Icon name="X" size={20} color="var(--color-foreground)" strokeWidth={2} aria-hidden="true" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-4 md:p-6 space-y-6"
          aria-label="Retirement plan form"
          noValidate
        >
          <AriaLiveRegion message={announcement} priority="assertive" />
          {/* Error Count Badge */}
          {touchedCount > 0 && errorCount > 0 && (
            <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-red-500 text-white text-xs font-bold flex-shrink-0">
                {errorCount}
              </div>
              <p className="text-sm text-red-700 font-medium">
                {errorCount} field{errorCount > 1 ? 's' : ''} require{errorCount === 1 ? 's' : ''} attention before submitting
              </p>
            </div>
          )}
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
            <div className="space-y-4">
              <FormInput
                label="Plan Name"
                type="text"
                required
                placeholder="e.g., My Retirement Plan"
                value={values?.planName}
                onChange={(e) => handleChange('planName', e?.target?.value)}
                onBlur={() => handleBlur('planName')}
                error={touched?.planName && errors?.planName}
                success={isFieldValid('planName')}
                maxLength={100}
                helpText="A descriptive name for this retirement scenario"
                disabled={calculating || isSubmitting}
                description=""
                icon=""
                prefix=""
                suffix=""
                ariaLabel="Plan Name"
                id="planName"
                name="planName"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormInput
                  label="Current Age"
                  type="number"
                  required
                  placeholder="e.g., 35"
                  value={values?.currentAge}
                  onChange={(e) => handleChange('currentAge', parseInt(e?.target?.value))}
                  onBlur={() => handleBlur('currentAge')}
                  error={touched?.currentAge && errors?.currentAge}
                  success={isFieldValid('currentAge')}
                  min="18"
                  max="100"
                  helpText="Age must be between 18 and 100"
                  disabled={calculating || isSubmitting}
                  description="18–100 years"
                  icon=""
                  prefix=""
                  suffix=""
                  ariaLabel="Current Age"
                  id="currentAge"
                  name="currentAge"
                  maxLength={undefined}
                />
                <FormInput
                  label="Retirement Age"
                  type="number"
                  required
                  placeholder="e.g., 65"
                  value={values?.retirementAge}
                  onChange={(e) => handleChange('retirementAge', parseInt(e?.target?.value))}
                  onBlur={() => handleBlur('retirementAge')}
                  error={touched?.retirementAge && errors?.retirementAge}
                  success={isFieldValid('retirementAge')}
                  min="50"
                  max="75"
                  helpText="Target retirement age (50–75). Must be greater than current age."
                  disabled={calculating || isSubmitting}
                  description="50–75 years"
                  icon=""
                  prefix=""
                  suffix=""
                  ariaLabel="Retirement Age"
                  id="retirementAge"
                  name="retirementAge"
                  maxLength={undefined}
                />
                <FormInput
                  label="Life Expectancy"
                  type="number"
                  required
                  placeholder="e.g., 90"
                  value={values?.lifeExpectancy}
                  onChange={(e) => handleChange('lifeExpectancy', parseInt(e?.target?.value))}
                  onBlur={() => handleBlur('lifeExpectancy')}
                  error={touched?.lifeExpectancy && errors?.lifeExpectancy}
                  success={isFieldValid('lifeExpectancy')}
                  min="70"
                  max="110"
                  helpText="Estimated life expectancy (70–110). Must be greater than retirement age."
                  disabled={calculating || isSubmitting}
                  description="70–110 years"
                  icon=""
                  prefix=""
                  suffix=""
                  ariaLabel="Life Expectancy"
                  id="lifeExpectancy"
                  name="lifeExpectancy"
                  maxLength={undefined}
                />
              </div>
            </div>
          </div>

          {/* Income & Expenses */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Income & Expenses</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Current Annual Income"
                type="number"
                required
                placeholder="e.g., 75000"
                value={values?.currentAnnualIncome}
                onChange={(e) => handleChange('currentAnnualIncome', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('currentAnnualIncome')}
                error={touched?.currentAnnualIncome && errors?.currentAnnualIncome}
                success={isFieldValid('currentAnnualIncome')}
                min="0"
                step="1000"
                prefix="$"
                helpText="Your current gross annual income in CAD"
                disabled={calculating || isSubmitting}
                description="Gross annual income in CAD"
                icon=""
                suffix=""
                ariaLabel="Current Annual Income"
                id="currentAnnualIncome"
                name="currentAnnualIncome"
                maxLength={undefined}
              />
              <FormInput
                label="Current Annual Expenses"
                type="number"
                required
                placeholder="e.g., 48000"
                value={values?.currentExpenses}
                onChange={(e) => handleChange('currentExpenses', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('currentExpenses')}
                error={touched?.currentExpenses && errors?.currentExpenses}
                success={isFieldValid('currentExpenses')}
                min="0"
                step="1000"
                prefix="$"
                helpText="Your current total annual expenses in CAD"
                disabled={calculating || isSubmitting}
                description="Total annual expenses in CAD"
                icon=""
                suffix=""
                ariaLabel="Current Annual Expenses"
                id="currentExpenses"
                name="currentExpenses"
                maxLength={undefined}
              />
              <FormInput
                label="Target Income Replacement"
                type="number"
                required
                placeholder="e.g., 70"
                value={values?.targetIncomeReplacementRatio}
                onChange={(e) => handleChange('targetIncomeReplacementRatio', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('targetIncomeReplacementRatio')}
                error={touched?.targetIncomeReplacementRatio && errors?.targetIncomeReplacementRatio}
                success={isFieldValid('targetIncomeReplacementRatio')}
                min="50"
                max="100"
                step="5"
                suffix="%"
                helpText="Percentage of current income needed in retirement. Typically 60–80%."
                description="Typically 60–80% of current income"
                disabled={calculating || isSubmitting}
                icon=""
                prefix=""
                ariaLabel="Target Income Replacement"
                id="targetIncomeReplacementRatio"
                name="targetIncomeReplacementRatio"
                maxLength={undefined}
              />
            </div>
          </div>

          {/* Savings & Contributions */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Savings & Contributions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="RRSP Balance"
                type="number"
                required
                placeholder="e.g., 125000"
                value={values?.rrspBalance}
                onChange={(e) => handleChange('rrspBalance', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('rrspBalance')}
                error={touched?.rrspBalance && errors?.rrspBalance}
                min="0"
                step="1000"
                prefix="$"
                suffix=""
                maxLength={undefined}
                helpText="Current Registered Retirement Savings Plan balance"
                disabled={calculating || isSubmitting}
                description="Current RRSP balance in CAD"
                icon=""
                ariaLabel="RRSP Balance"
                id="rrspBalance"
                name="rrspBalance"
              />
              <FormInput
                label="Monthly RRSP Contribution"
                type="number"
                required
                placeholder="e.g., 500"
                value={values?.monthlyRrspContribution}
                onChange={(e) => handleChange('monthlyRrspContribution', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('monthlyRrspContribution')}
                error={touched?.monthlyRrspContribution && errors?.monthlyRrspContribution}
                min="0"
                step="50"
                prefix="$"
                suffix=""
                maxLength={undefined}
                helpText="Monthly contribution to your RRSP account"
                disabled={calculating || isSubmitting}
                description="Monthly RRSP contribution in CAD"
                icon=""
                ariaLabel="Monthly RRSP Contribution"
                id="monthlyRrspContribution"
                name="monthlyRrspContribution"
              />
              <FormInput
                label="TFSA Balance"
                type="number"
                required
                placeholder="e.g., 45000"
                value={values?.tfsaBalance}
                onChange={(e) => handleChange('tfsaBalance', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('tfsaBalance')}
                error={touched?.tfsaBalance && errors?.tfsaBalance}
                min="0"
                step="1000"
                prefix="$"
                suffix=""
                maxLength={undefined}
                helpText="Current Tax-Free Savings Account balance"
                disabled={calculating || isSubmitting}
                description="Current TFSA balance in CAD"
                icon=""
                ariaLabel="TFSA Balance"
                id="tfsaBalance"
                name="tfsaBalance"
              />
              <FormInput
                label="Monthly TFSA Contribution"
                type="number"
                required
                placeholder="e.g., 300"
                value={values?.monthlyTfsaContribution}
                onChange={(e) => handleChange('monthlyTfsaContribution', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('monthlyTfsaContribution')}
                error={touched?.monthlyTfsaContribution && errors?.monthlyTfsaContribution}
                min="0"
                step="50"
                prefix="$"
                suffix=""
                maxLength={undefined}
                helpText="Monthly contribution to your TFSA account"
                disabled={calculating || isSubmitting}
                description="Monthly TFSA contribution in CAD"
                icon=""
                ariaLabel="Monthly TFSA Contribution"
                id="monthlyTfsaContribution"
                name="monthlyTfsaContribution"
              />
              <FormInput
                label="Other Savings"
                type="number"
                required
                placeholder="e.g., 15000"
                value={values?.otherSavings}
                onChange={(e) => handleChange('otherSavings', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('otherSavings')}
                error={touched?.otherSavings && errors?.otherSavings}
                min="0"
                step="1000"
                prefix="$"
                suffix=""
                maxLength={undefined}
                helpText="Other savings not in RRSP or TFSA (non-registered accounts, etc.)"
                disabled={calculating || isSubmitting}
                description="Non-registered savings in CAD"
                icon=""
                ariaLabel="Other Savings"
                id="otherSavings"
                name="otherSavings"
              />
            </div>
          </div>

          {/* Government Benefits */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Government Benefits & Pension</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormInput
                label="CPP Monthly"
                type="number"
                required
                placeholder="e.g., 1200"
                value={values?.cppEstimatedMonthly}
                onChange={(e) => handleChange('cppEstimatedMonthly', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('cppEstimatedMonthly')}
                error={touched?.cppEstimatedMonthly && errors?.cppEstimatedMonthly}
                min="0"
                step="50"
                prefix="$"
                suffix=""
                maxLength={undefined}
                helpText="Estimated monthly Canada Pension Plan benefit. Max CPP in 2024 is ~$1,364/month."
                description="Canada Pension Plan estimate"
                disabled={calculating || isSubmitting}
                icon=""
                ariaLabel="CPP Monthly"
                id="cppEstimatedMonthly"
                name="cppEstimatedMonthly"
              />
              <FormInput
                label="OAS Monthly"
                type="number"
                required
                placeholder="e.g., 700"
                value={values?.oasEstimatedMonthly}
                onChange={(e) => handleChange('oasEstimatedMonthly', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('oasEstimatedMonthly')}
                error={touched?.oasEstimatedMonthly && errors?.oasEstimatedMonthly}
                min="0"
                step="50"
                prefix="$"
                suffix=""
                maxLength={undefined}
                helpText="Estimated monthly Old Age Security benefit. Max OAS in 2024 is ~$707/month."
                description="Old Age Security estimate"
                disabled={calculating || isSubmitting}
                icon=""
                ariaLabel="OAS Monthly"
                id="oasEstimatedMonthly"
                name="oasEstimatedMonthly"
              />
              <FormInput
                label="Employer Pension Monthly"
                type="number"
                required
                placeholder="e.g., 0"
                value={values?.employerPensionMonthly}
                onChange={(e) => handleChange('employerPensionMonthly', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('employerPensionMonthly')}
                error={touched?.employerPensionMonthly && errors?.employerPensionMonthly}
                min="0"
                step="50"
                prefix="$"
                suffix=""
                maxLength={undefined}
                helpText="Monthly defined benefit pension from employer, if applicable"
                disabled={calculating || isSubmitting}
                description="Defined benefit pension (if any)"
                icon=""
                ariaLabel="Employer Pension Monthly"
                id="employerPensionMonthly"
                name="employerPensionMonthly"
              />
            </div>
          </div>

          {/* Assumptions */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Assumptions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                label="Inflation Rate"
                type="number"
                required
                placeholder="e.g., 2.5"
                value={values?.inflationRate}
                onChange={(e) => handleChange('inflationRate', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('inflationRate')}
                error={touched?.inflationRate && errors?.inflationRate}
                min="0"
                max="10"
                step="0.1"
                prefix=""
                suffix="%"
                maxLength={undefined}
                helpText="Expected annual inflation rate. Bank of Canada targets 2%. Typical range: 1.5–3%."
                disabled={calculating || isSubmitting}
                description="Typically 1.5–3% (Bank of Canada target: 2%)"
                icon=""
                ariaLabel="Inflation Rate"
                id="inflationRate"
                name="inflationRate"
              />
              <FormInput
                label="Expected Return Rate"
                type="number"
                required
                placeholder="e.g., 6.0"
                value={values?.expectedReturnRate}
                onChange={(e) => handleChange('expectedReturnRate', parseFloat(e?.target?.value))}
                onBlur={() => handleBlur('expectedReturnRate')}
                error={touched?.expectedReturnRate && errors?.expectedReturnRate}
                min="0"
                max="20"
                step="0.1"
                prefix=""
                suffix="%"
                maxLength={undefined}
                helpText="Projected annual return on investments. Typically 3–8% for balanced portfolios."
                disabled={calculating || isSubmitting}
                description="Projected annual return 3–8%"
                icon=""
                ariaLabel="Expected Return Rate"
                id="expectedReturnRate"
                name="expectedReturnRate"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
              disabled={calculating || isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName={calculating || isSubmitting ? 'Loader2' : 'Check'}
              iconPosition="left"
              fullWidth
              disabled={!isFormValid || calculating || isSubmitting}
              disabledTooltip={!isFormValid ? 'Please fix all errors before submitting' : undefined}
            >
              {calculating || isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Icon name="Loader2" size={16} color="currentColor" strokeWidth={2} className="animate-spin" />
                  {plan ? 'Updating...' : 'Calculating...'}
                </span>
              ) : (
                plan ? 'Update Plan' : 'Calculate Retirement'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RetirementForm;